import {
  TemplateSummary,
  CreateTemplateSummaryInput,
  UpdateTemplateSummaryInput
} from '@/types/templateSummaryTypes';

const API_ROOT = import.meta.env.VITE_API_BASE_URL || '/api';

type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: any,
  config?: any
) => Promise<string>;

const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token)
        throw new Error('Failed to acquire token for template summaries');
      return token;
    }
    throw error;
  }
};

const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<Response> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {})
    }
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    throw new Error(
      `API request failed: ${response.status} ${response.statusText} ${errorText}`
    );
  }
  return response;
};

/**
 * Creates a new template summary
 */
export const createTemplateSummary = async (
  input: CreateTemplateSummaryInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateSummary> => {
  const requestBody = {
    name: input.name,
    description: input.description,
    prompt: input.prompt,
    order: input.order,
    isEnabled: input.isEnabled,
    distributionGroupIds: input.distributionGroupIds || []
  };

  const response = await makeAuthenticatedRequest(
    `${API_ROOT}/templates/${input.templateId}/summaries`,
    {
      method: 'POST',
      body: JSON.stringify(requestBody),
      headers: {
        'X-User-Id': userId
      }
    },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  return await response.json();
};

/**
 * Updates an existing template summary
 */
export const updateTemplateSummary = async (
  templateId: number,
  input: UpdateTemplateSummaryInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateSummary> => {
  // Server expects body without the id (it's in the route)
  const requestBody = {
    name: input.name,
    description: input.description,
    prompt: input.prompt,
    order: input.order,
    isEnabled: input.isEnabled,
    distributionGroupIds: input.distributionGroupIds
  };

  const response = await makeAuthenticatedRequest(
    `${API_ROOT}/templates/${templateId}/summaries/${input.id}`,
    {
      method: 'PUT',
      body: JSON.stringify(requestBody),
      headers: {
        'X-User-Id': userId
      }
    },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  return await response.json();
};

/**
 * Deletes a template summary
 */
export const deleteTemplateSummary = async (
  templateId: number,
  summaryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  await makeAuthenticatedRequest(
    `${API_ROOT}/templates/${templateId}/summaries/${summaryId}`,
    {
      method: 'DELETE',
      headers: {
        'X-User-Id': userId
      }
    },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );
};

/**
 * Gets all template summaries for a template
 */
export const getTemplateSummaries = async (
  templateId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateSummary[]> => {
  const response = await makeAuthenticatedRequest(
    `${API_ROOT}/templates/${templateId}/summaries`,
    {
      method: 'GET',
      headers: {
        'X-User-Id': userId
      }
    },
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  return await response.json();
};
